import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import { getUniqueErrorMessages, LOGISTICS_FIELD_NAMES } from './schema';
import Panel from './Panel';

function ToggleButton({
 on, icon, hasError, onClick, children,
}) {
  const buttonClassName = classNames({
    'btn-outline-primary': !hasError && on,
    'btn-outline-secondary': !hasError && !on,
    'btn-outline-danger': hasError,
  });

  return (
    <button type="button" className={`btn btn-sm ${buttonClassName}`} onClick={onClick}>
      <i className={`${on ? 'fa-solid fa-check-square' : 'fa-light fa-square'} me-2`} />
      {icon && <i className={`fa fa-${icon} me-1`} />}
      <span className="text-body"> {children}</span>
    </button>
  );
}

ToggleButton.propTypes = {
  on: PropTypes.bool.isRequired,
  icon: PropTypes.string,
  hasError: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

ToggleButton.defaultProps = {
  icon: null,
  hasError: false,
};

function LogisticDetails({
  equipmentNotes,
  equipmentNotesDisplay,
  logisticsInPerson,
  logisticsLive,
  logisticsPreRecorded,
  logisticsRemote,
  logisticsVideo,
  logisticsUrl,
  markFieldTouched,
  toggleParam,
  useUpdateParamWithEvent,
  validationActive,
  validationErrors,
}) {
  // Get unique error messages for all logistics fields
  const logisticsErrorMessages = validationErrors
    ? getUniqueErrorMessages(validationErrors, LOGISTICS_FIELD_NAMES)
    : [];

  return (
    <Panel>
      <div>
        <div className="mb-4">
          <p className="mb-2">
            <b>Logistics</b> <i className="text-secondary">Select all that apply</i>
          </p>

          <div className="d-flex flex-shrink-0 flex-wrap gap-2">
            <ToggleButton
              icon="home"
              hasError={Boolean(validationErrors?.logisticsRemote)}
              on={logisticsRemote}
              onClick={() => toggleParam('logisticsRemote')}
            >
              Remote
            </ToggleButton>

            <ToggleButton
              icon="user"
              hasError={Boolean(validationErrors?.logisticsInPerson)}
              on={logisticsInPerson}
              onClick={() => toggleParam('logisticsInPerson')}
            >
              In Person
            </ToggleButton>

            <ToggleButton
              icon="video"
              hasError={Boolean(validationErrors?.logisticsVideo)}
              on={logisticsVideo}
              onClick={() => toggleParam('logisticsVideo')}
            >
              Video
            </ToggleButton>

            <ToggleButton
              icon="file-video"
              hasError={Boolean(validationErrors?.logisticsPreRecorded)}
              on={logisticsPreRecorded}
              onClick={() => toggleParam('logisticsPreRecorded')}
            >
              Pre-Recorded
            </ToggleButton>

            <ToggleButton
              icon="camcorder"
              hasError={Boolean(validationErrors?.logisticsLive)}
              on={logisticsLive}
              onClick={() => toggleParam('logisticsLive')}
            >
              Live
            </ToggleButton>
          </div>
          {logisticsErrorMessages.length > 0 && (
            <div className="invalid-feedback d-block mt-2">
              {logisticsErrorMessages.map((msg) => (
                <div key={msg}>{msg}</div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-2">
          <h4 className="mb-2 paragraph-md">
            <span className="fw-semibold">Equipment Notes</span> <i className="text-secondary">(optional)</i>
          </h4>

          <textarea
            className={classNames('form-control mb-1', {
              'is-invalid': !!validationErrors?.equipmentNotes,
              'is-valid': validationActive && !validationErrors?.equipmentNotes,
            })}
            value={equipmentNotes}
            onChange={useUpdateParamWithEvent('equipmentNotes')}
            onBlur={() => markFieldTouched('equipmentNotes')}
            style={{ height: '132px' }}
            placeholder="Outline the equipment guests need to bring or note of any other equipment details."
          />

          {validationErrors?.equipmentNotes && (
            <div className="invalid-feedback d-block mt-1">{validationErrors.equipmentNotes}</div>
          )}

          <div className="paragraph-sm text-neutral-60 text-end">{equipmentNotesDisplay}</div>
        </div>

        <div>
          <h4 className="mb-2 paragraph-md">
            <span className="fw-semibold">Links</span> <i className="text-secondary">(optional)</i>
          </h4>

          <input
            className="form-control"
            placeholder="Enter a URL"
            value={logisticsUrl}
            onChange={useUpdateParamWithEvent('logisticsUrl')}
          />
        </div>
      </div>
    </Panel>
  );
}

LogisticDetails.propTypes = {
  equipmentNotes: PropTypes.string,
  equipmentNotesDisplay: PropTypes.string,
  logisticsInPerson: PropTypes.bool,
  logisticsLive: PropTypes.bool,
  logisticsPreRecorded: PropTypes.bool,
  logisticsRemote: PropTypes.bool,
  logisticsVideo: PropTypes.bool,
  logisticsUrl: PropTypes.string,
  markFieldTouched: PropTypes.func.isRequired,
  toggleParam: PropTypes.func.isRequired,
  useUpdateParamWithEvent: PropTypes.func.isRequired,
  validationActive: PropTypes.bool,
  validationErrors: PropTypes.shape({
    logisticsRemote: PropTypes.string,
    logisticsInPerson: PropTypes.string,
    logisticsVideo: PropTypes.string,
    logisticsPreRecorded: PropTypes.string,
    logisticsLive: PropTypes.string,
    equipmentNotes: PropTypes.string,
  }),
};

LogisticDetails.defaultProps = {
  equipmentNotes: '',
  equipmentNotesDisplay: '',
  logisticsInPerson: false,
  logisticsLive: false,
  logisticsPreRecorded: false,
  logisticsRemote: false,
  logisticsVideo: false,
  logisticsUrl: '',
  validationActive: false,
  validationErrors: {},
};

export default LogisticDetails;
